import { extractValueByIndexType } from 'components/Reports/utils'
import { getFormattedStringValueWithSymbol } from 'hooks/useFormattedStringValue'
import { isNumber } from 'lodash'
import { ReportConfig, TextValue } from 'redux/reducers/reports'

export const extractNumberFromString = (value: string) => {
  const numericPart = value.replace(/[^\d.-]/g, '')
  const number = parseFloat(numericPart)
  return isNaN(number) ? null : number
}

export const universalSort = (rowA: any, rowB: any, columnId: number) => {
  let firstValue = rowA.values[columnId].value
  let secondValue = rowB.values[columnId].value

  let firstNumber = extractNumberFromString(firstValue)
  let secondNumber = extractNumberFromString(secondValue)

  if (firstNumber !== null && secondNumber !== null) {
    return firstNumber > secondNumber ? 1 : firstNumber < secondNumber ? -1 : 0
  }

  return firstValue.replace(' ', '').localeCompare(secondValue.replace(' ', ''))
}

export const customGlobalFilter = (rows: any, collumnId: number, filterValue: string) => {
  return rows.filter((row: any) => {
    return row.cells.some((cell: any) => {
      return cell?.value?.value !== undefined
        ? String(cell.value.value).toLowerCase().includes(filterValue.toLowerCase())
        : false
    })
  })
}

export const transformDataForTable = (
  reportData: any,
  tableRows: ReportConfig[],
  locale: string,
  translations: object,
  groupId?: TextValue | null,
  subRetailId?: TextValue | null
): any[] => {
  const transformedRows = reportData.map((report: any) => {
    const row = tableRows.map((tableRow) => {
      const properties = tableRow.properties
      let cellData: {
        value: any
        isTag: boolean
        groupId?: string | null
        subRetailId?: string | null
      } = {
        value: null,
        isTag: false,
        groupId: null,
        subRetailId: null,
      }

      if (properties) {
        const text: string = extractValueByIndexType(properties.text_val, report)
        let numVal: number = extractValueByIndexType(properties.num_val, report)
        const numValCurrSymbol: string | null = extractValueByIndexType(properties.num_curr_symbol, report) ?? null
        const numValSymbolType: number | null = extractValueByIndexType(properties.num_symbol_type, report)
        const numValIsRound = properties?.num_is_round ?? false
        const compValue: number | null = extractValueByIndexType(properties.compare_val, report) ?? null
        const compValueCurrSymbol: string | null =
          extractValueByIndexType(properties.compare_curr_symbol, report) ?? null
        const compValueSymbolType: number | null = extractValueByIndexType(properties?.compare_symbol_type, report)
        const compValueOffset: number | null = extractValueByIndexType(properties?.compare_offset, report)
        const subRetailIdValue: string | null = extractValueByIndexType(subRetailId, report) ?? null
        const groupIdValue: string | null = extractValueByIndexType(groupId, report) ?? null

        if (text) {
          const translated = (translations as any)?.[locale]?.[text]
          cellData.value = translated ?? text
          cellData.subRetailId = subRetailIdValue
          cellData.groupId = groupIdValue
        }

        if (isNumber(numVal)) {
          cellData.value = getFormattedStringValueWithSymbol(
            numVal,
            numValCurrSymbol,
            numValSymbolType,
            numValIsRound,
            translations,
            locale
          )
          cellData.subRetailId = subRetailIdValue
          cellData.groupId = groupIdValue
        }

        if (isNumber(compValue)) {
          cellData.value = getFormattedStringValueWithSymbol(
            compValue + (compValueOffset ?? 0),
            compValueCurrSymbol,
            compValueSymbolType,
            numValIsRound,
            translations,
            locale
          )
          cellData.isTag = true
          cellData.subRetailId = subRetailIdValue
          cellData.groupId = groupIdValue
        }

        if (!text && numVal === null && compValue === null) {
          cellData.value = ''
          cellData.subRetailId = subRetailIdValue
          cellData.groupId = groupIdValue
        }
      }

      return cellData
    })

    return row
  })

  return transformedRows
}

export const cleanValueForExport = (value: string | null | undefined): string => {
  if (!value || typeof value !== 'string') {
    return value || ''
  }

  return value.replace(/[₪$€£¥₹₽₩₦₨₩₪₫₭₮₯₰₱₲₳₴₵₶₷₸₹₺₻₼₽₾₿%+]/g, '').trim()
}

const parseCleanedNumberIfPossible = (value: string | number): number | string => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : ''
  }

  let str = value.replace(/\u00A0/g, ' ').trim()
  str = str.replace(/\u200E|\u200F|\u200C|\u200D|\uFEFF|\u2066|\u2067|\u2068|\u2069|\u202A|\u202B|\u202C/g, '')
  if (str === '' || /^[-–—]$/.test(str)) return ''

  let isNegative = false
  if (str.startsWith('(') && str.endsWith(')')) {
    isNegative = true
    str = str.slice(1, -1)
  }

  str = str.replace(/[ \t'’_]/g, '')

  const hasDot = str.includes('.')
  const hasComma = str.includes(',')

  if (hasDot && hasComma) {
    const lastDot = str.lastIndexOf('.')
    const lastComma = str.lastIndexOf(',')
    const decimalSep = lastDot > lastComma ? '.' : ','
    const thousandSep = decimalSep === '.' ? ',' : '.'
    str = str.replace(new RegExp('\\' + thousandSep, 'g'), '')
    str = str.replace(new RegExp('\\' + decimalSep, 'g'), '.')
  } else if (hasComma && !hasDot) {
    // 1,234,567 -> 1234567,  or 12,5 -> 12.5
    if (/^-?\d{1,3}(,\d{3})+$/.test(str)) {
      str = str.replace(/,/g, '')
    } else {
      str = str.replace(/,/g, '.')
    }
  } else if (hasDot && !hasComma) {
    // 1.234.567 -> 1234567
    if (/^-?\d{1,3}(\.\d{3})+$/.test(str)) {
      str = str.replace(/\./g, '')
    }
  } else {
    // In case of single commas, remove them
    str = str.replace(/,/g, '')
  }

  if (str === '' || str === '-') return ''
  const num = Number(str)
  if (Number.isFinite(num)) return isNegative ? -num : num
  return value
}

export const transformDataForExport = (data: any[], includeSymbols: boolean = false): any[] => {
  if (includeSymbols) {
    return data
  }

  return data.map((row) =>
    row.map((cell: any) => {
      if (Array.isArray(cell)) {
        return cell.map((item: any) => {
          if (typeof item.value === 'string' && item.value.includes('\n') && /^\d+(\n\d+)*$/.test(item.value)) {
            return {
              ...item,
              value: item.value,
            }
          }
          const cleaned = cleanValueForExport(item.value)
          const parsed = parseCleanedNumberIfPossible(cleaned)
          return {
            ...item,
            value: parsed,
          }
        })
      }

      if (typeof cell.value === 'string' && cell.value.includes('\n') && /^\d+(\n\d+)*$/.test(cell.value)) {
        return {
          ...cell,
          value: cell.value,
        }
      }

      const cleaned = cleanValueForExport(cell.value)
      const parsed = parseCleanedNumberIfPossible(cleaned)
      return {
        ...cell,
        value: parsed,
      }
    })
  )
}
